import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';

const UserSidebar = ({ activeTab, setActiveTab, isMobileMenuOpen, setIsMobileMenuOpen }) => {
  const { getCartCount } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Define menu items
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/dashboard' },
    { id: 'products', label: 'Products', icon: '📦', path: '/user/products' },
    { id: 'cart', label: 'Cart', icon: '🛒', path: '/user/cart', badge: getCartCount() },
    { id: 'purchases', label: 'Purchases', icon: '🛍️', path: '/user/purchases' },
    { id: 'downline', label: 'My Team', icon: '👥', path: '/user/downline' },
    { id: 'team-15', label: '15-Level Team', icon: '🌳', path: '/user/team-15' },
    { id: 'bonanza', label: 'Bonanza Tracker', icon: '🎯', path: '/user/bonanza' },
    { id: 'investment-plans', label: 'Monthly Product Reward', icon: '💼', path: '/user/investment-plans' },
    { id: 'lifetime-rewards', label: 'Lifetime Rewards', icon: '🌟', path: '/user/lifetime-rewards' },
    { id: 'ranks', label: 'Ranks', icon: '🏆', path: '/user/ranks' },
    { id: 'income', label: 'Income', icon: '💰', path: '/user/income' },
    { id: 'payouts', label: 'Payouts', icon: '💸', path: '/user/payouts' },
    { id: 'send-money', label: 'Send Money', icon: '📲', path: '/user/send-money' },
    { id: 'fund-transfer', label: 'Fund Transfer', icon: '💸', path: '/user/fund-transfer' },
    { id: 'fund-transfer-history', label: 'Transfer History', icon: '📊', path: '/user/fund-transfer-history' },
    // { id: 'main-to-deposit', label: 'Main to Deposit', icon: '🔁', path: '/user/main-to-deposit' },
    { id: 'kyc', label: 'KYC', icon: '🆔', path: '/user/kyc' },
    { id: 'reset-password', label: 'Reset Password', icon: '🔐', path: '/user/reset-password' },
    // { id: 'settings', label: 'Settings', icon: '⚙️', path: '/user/settings' },
  ];

  // Handle tab click
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle copy referral link
  const handleCopyLink = () => {
    const referralLink = `https://www.desimart.net/register?sponserid=${user?.mySponsorCode || ''}`;
    navigator.clipboard.writeText(referralLink);
    alert("Referral link copied!");
  };

  // Handle keyboard events for accessibility
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleCopyLink();
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu overlay"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 lg:relative lg:flex lg:flex-shrink-0 z-50 lg:z-auto
          transform transition-transform duration-300 ease-in-out
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-label="User dashboard sidebar"
        role="navigation"
      >
        <div className="flex flex-col w-64 sm:w-72 md:w-80 h-screen bg-white shadow-lg">
          {/* Header Section */}
          <div className="flex items-center flex-shrink-0 px-3 sm:px-4 py-4 sm:py-6 bg-gradient-to-r from-[#0F2F3D] to-[#E4572E]">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center min-w-0 flex-1">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white flex items-center justify-center font-bold text-[#0F2F3D] text-sm sm:text-base flex-shrink-0">
                  {user?.firstName?.charAt(0) || 'U'}
                </div>
                <div className="ml-2 sm:ml-3 truncate">
                  <p className="text-xs sm:text-sm font-medium text-white truncate">
                    {user?.firstName ? `${user.firstName}'s Panel` : 'User Panel'}
                  </p>
                  <p className="text-xs text-white/80 truncate">Desi-Mart</p>
                </div>
              </div>

              {/* Mobile Close Button */}
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="lg:hidden text-white hover:text-gray-200 p-1 ml-2 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-white rounded"
                aria-label="Close menu"
              >
                <svg
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 flex flex-col overflow-y-auto">
            {/* Referral Link Button */}
            <div className="px-3 sm:px-4 py-3 sm:py-4 border-b border-gray-100">
              <button
                onClick={handleCopyLink}
                onKeyDown={handleKeyDown}
                className="group relative w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white font-semibold text-xs sm:text-sm hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 cursor-pointer flex items-center justify-center gap-1.5 sm:gap-2 border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#E4572E] focus:ring-offset-2"
                aria-label="Copy referral link"
              >
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 transition-transform group-hover:rotate-12 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span className="truncate">Copy Referral Link</span>

                {/* Shine effect - hidden on mobile */}
                <div className="hidden sm:block absolute inset-0 rounded-lg bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              </button>
            </div>

            {/* Navigation Menu */}
            <nav className="flex-1 px-2 sm:px-3 py-2 sm:py-4 space-y-0.5 sm:space-y-1">
              {menuItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => {
                    handleTabClick(item.id);
                    // Auto close menu on mobile after selection
                    if (window.innerWidth < 1024) {
                      setIsMobileMenuOpen(false);
                    }
                  }}
                  className={`
                    w-full group flex items-center justify-between 
                    px-2 sm:px-3 py-2.5 sm:py-3 
                    text-xs sm:text-sm font-medium 
                    rounded-lg transition-all duration-200
                    ${activeTab === item.id
                      ? 'bg-[#0F2F3D] text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 active:bg-gray-100'
                    }
                    focus:outline-none focus:ring-2 focus:ring-[#0F2F3D] focus:ring-offset-2
                  `}
                  aria-current={activeTab === item.id ? 'page' : undefined}
                >
                  <div className="flex items-center min-w-0">
                    <span className="mr-2 sm:mr-3 text-base sm:text-lg flex-shrink-0" aria-hidden="true">
                      {item.icon}
                    </span>
                    <span className="truncate">{item.label}</span>
                  </div>
                  {item.badge > 0 && (
                    <span
                      className="bg-[#E4572E] text-white text-[10px] sm:text-xs font-bold rounded-full min-w-[18px] sm:min-w-[20px] h-4 sm:h-5 flex items-center justify-center px-1 ml-1 flex-shrink-0"
                      aria-label={`${item.badge} items in ${item.label}`}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              ))}
              <button
                onClick={handleLogout}
                className="px-5 py-2.5 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-medium text-sm tracking-wide"
              >
                Sign Out
              </button>
            </nav>
          </div>

          {/* Footer - Back to Home Link */}
          <div className="flex-shrink-0 border-t border-gray-200 p-3 sm:p-4">
            <Link
              to="/"
              className="flex-shrink-0 w-full group block focus:outline-none focus:ring-2 focus:ring-[#0F2F3D] focus:ring-offset-2 rounded"
              onClick={() => window.innerWidth < 1024 && setIsMobileMenuOpen(false)}
            >
              <div className="flex items-center">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                    Back to Home
                  </p>
                  <p className="text-[10px] sm:text-xs font-medium text-gray-500 group-hover:text-gray-700 truncate">
                    Exit dashboard
                  </p>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserSidebar;